/** Service Desk console: Field visits (C11 dispatch board, calendar and list; C12 visit detail), SD-S3-10. */
export const serviceVisits = {
  subtitle: "Dispatch engineers, see the month at a glance and keep the unscheduled queue empty.",
  noOrganisation: "No organisation",
  windowText: "{{day}} · {{from}} to {{to}}",
  actions: { schedule: "Schedule visit" },
  orgTab: { viewAll: "Open in Field visits", emptyTitle: "No field visits", emptyBody: "Visits scheduled for this organisation appear here." },
  stats: {
    label: "Visit figures",
    today: "Today",
    todayHint: "visits on the board",
    week: "This week",
    weekHint: "Monday to Sunday",
    road: "On the road",
    roadHint: "dispatched or on site today",
    queue: "Unscheduled",
    queueHint: "waiting for a window",
  },
  tabs: { label: "Visit views", dispatch: "Dispatch", calendar: "Calendar", list: "List" },
  status: {
    proposed: "Waiting for a window",
    scheduled: "Scheduled",
    dispatched: "Dispatched",
    on_site: "Checked in",
    completed: "Completed",
    cannot_complete: "Could not complete",
    cancelled: "Cancelled",
    no_access: "No access",
  },
  flow: { scheduled: "Scheduled", dispatched: "Dispatched", on_site: "Checked in", completed: "Completed" },
  kind: { corrective: "Corrective", pm: "Preventive maintenance", calibration: "Calibration", installation: "Installation", inspection: "Inspection", training: "Training" },
  origin: { staff: "Scheduled by the team", client: "Asked for by the client", maintenance: "From a maintenance plan" },
  error: { title: "Field visits couldn't load", body: "Check your connection and try again. Nothing was changed." },
  errors: {
    visit_conflict: "The engineer already has a visit in that window.",
    visit_transition: "The visit cannot make that move now.",
    visit_needs_window: "Give the visit a start and an end time first.",
    visit_needs_engineer: "Choose the lead engineer before scheduling the visit.",
    window_unavailable: "That window was taken. Pick another one.",
    too_late_to_change: "It is too late to change this visit.",
    report_locked: "The report was submitted and can no longer change.",
    report_incomplete: "The report is not complete yet.",
    beyond_contract: "This visit is beyond the contract.",
    visit_ended: "This visit has ended and can no longer change.",
    reason_needed: "Say why (a few words are enough).",
  },
  board: {
    label: "Dispatch board",
    loading: "Loading the dispatch board",
    dayGroup: "Day",
    previousDay: "Previous day",
    nextDay: "Next day",
    today: "Today",
    engineerFilter: "Engineer",
    allEngineers: "All engineers",
    engineer: "Engineer",
    day: "Day",
    time: "Start time",
    timeRange: "{{from}} to {{to}}",
    hour: "Hour",
    booked: "{{percent}}% booked",
    free: "Free all day",
    noEngineers: "No engineer can work visits yet. Give a team member the visits.perform right to see them here.",
    hint: "Drag a visit to another engineer or time, or use Move to… on the visit. The client is told after you confirm.",
    hintReadOnly: "You can see the board but not change it.",
    columnLabel: "{{name}}, {{day}}",
    dropLabel: "{{name}}'s day, drop a visit here",
    blockLabel: "{{number}}, {{organisation}}, {{from}} to {{to}}, {{status}}",
    moveTo: "Move {{number}} to…",
    moveMenu: "Move {{number}}",
    moveHeading: "Move {{number}} to…",
    move: "Move",
    selected: "{{number}} selected. Choose an engineer, a day and a start time, then press Move.",
    moveCancelled: "Move of {{number}} cancelled.",
    moved: "{{number}} moved to {{where}}. The client was told.",
    confirmTitle: "Reschedule {{number}}?",
    confirmWhere: "{{engineer}}, {{day}}, {{from}} to {{to}}",
    confirmBody: "Move to {{where}}. The client contact is told by e-mail with a new calendar invite.",
    confirm: "Reschedule and notify",
    noConflict: "No conflict for {{name}}.",
    conflict: "Conflict for {{name}}:",
  },
  availability: {
    title: "Who is free over the next {{count}} working days",
    hint: "Morning 08:00 to 12:00, afternoon 13:00 to 17:00. Pick a free half day to schedule.",
    label: "Engineer availability",
    am: "AM",
    pm: "PM",
    freeFor: "{{name}} is free on {{day}}, {{half}}",
    busyFor: "{{name}} is busy on {{day}}, {{half}}",
    schedule: "Schedule {{name}} on {{day}}, {{half}}",
    errorTitle: "Availability couldn't load",
  },
  queue: {
    title: "Unscheduled queue",
    emptyTitle: "Nothing waiting",
    emptyBody: "Every requested visit has a window.",
    schedule: "Schedule",
    scheduleOne: "Schedule {{number}}",
    waitingClient: "Waiting for the client to choose a window",
    waitingEngineer: "Needs an engineer and a window",
  },
  calendar: {
    loading: "Loading the calendar",
    monthGroup: "Month",
    previous: "Previous month",
    next: "Next month",
    thisMonth: "This month",
    openDay: "Open {{day}} on the dispatch board, {{count}} visits",
    more: "+{{count}} more",
    agenda: "Agenda for {{month}}",
    emptyTitle: "Nothing this month",
    emptyBody: "No visits for these filters. Pick another month or clear a filter.",
  },
  list: {
    label: "Field visits",
    loading: "Loading visits",
    statusGroup: "Status",
    chip: { all: "All", scheduled: "Scheduled", dispatched: "Dispatched", proposed: "Waiting", completed: "Completed" },
    search: "Search visit, ticket, organisation",
    searchLabel: "Search visits",
    visit: "Visit",
    ticket: "Ticket",
    organisation: "Organisation",
    kind: "Kind",
    engineer: "Engineer",
    window: "Window",
    status: "Status",
    noEngineer: "No engineer yet",
    clientChooses: "Client chooses",
    emptyTitle: "No visits scheduled",
    emptyBody: "Schedule the first visit from a ticket, or from the button above.",
    emptyFilteredTitle: "No visits match",
    emptyFilteredBody: "Clear a filter to see every visit.",
  },
  schedule: {
    title: "Schedule visit",
    titleQueued: "Schedule {{number}}",
    titleMove: "Reschedule {{number}}",
    description: "Pick the ticket, the engineer and the window. The warnings below update as you change the form.",
    descriptionMove: "The client is told about the new window.",
    ticket: "Ticket",
    ticketHint: "Type the ticket number or a few words of its title.",
    ticketPlaceholder: "TKT-10001 or part of the title",
    ticketResults: "Matching tickets",
    ticketNone: "No open ticket matches.",
    ticketError: "Tickets couldn't be searched. Try again.",
    changeTicket: "Change",
    kind: "Kind",
    engineer: "Engineer",
    engineerChoose: "Choose an engineer",
    theEngineer: "The engineer",
    site: "Site",
    siteAny: "No particular site",
    assets: "Equipment",
    assetsNone: "This organisation has no registered equipment.",
    window: "Window",
    mode: { fixed: "Set the window", client: "Let the client choose (up to 3 windows)", queue: "Decide later (add to the queue)" },
    date: "Date",
    start: "Start",
    length: "Length",
    lengthValue: "{{hours}} h {{minutes}} min",
    clientHint: "The client picks one of up to {{count}} windows in chat, the portal or by e-mail.",
    pickEngineerFirst: "Choose the engineer first to see their open windows.",
    windowsLabel: "Open windows",
    windowsNone: "No open window in the next ten working days for this length. Shorten the visit or choose another engineer.",
    windowsError: "Open windows couldn't load. Try again.",
    calendarUnavailable: "The connected calendar couldn't be read, so only other visits were compared.",
    chosen: "{{count}} of 3 windows chosen",
    queueHint: "The visit waits in the unscheduled queue until someone gives it a window.",
    prep: "Preparation note for the client",
    prepHint: "Shown on the visit the client sees.",
    sunday: "Field visits run Monday to Saturday. Pick another day.",
    afterHours: "The visit starts or ends outside 08:00 to 17:00. You can still schedule it.",
    conflictTitle: "Conflict for {{name}}:",
    conflictVisit: "{{number}} from {{from}} to {{to}}",
    conflictCalendar: "calendar busy from {{from}} to {{to}}",
    allowConflicts: "Schedule anyway (the engineer will be double booked)",
    free: "{{name}} is free on {{day}} from {{from}}.",
    covered: "Covered by {{contract}}. The visit counts against its entitlements.",
    notCovered: "No active contract covers this ticket: the visit is chargeable unless the warranty applies.",
    notify: "The client was told about {{when}}; they are told again.",
    noWindow: "no window yet",
    submit: "Schedule visit",
    submitPropose: "Offer these windows",
    submitQueue: "Add to the queue",
    submitQueued: "Schedule visit",
    submitMove: "Reschedule and notify",
    problems: {
      ticket: "Pick the ticket this visit is for.",
      engineer: "Choose the engineer.",
      day: "Choose a date.",
      sunday: "Field visits run Monday to Saturday.",
      start: "Choose a start time.",
      duration: "Choose a length between 15 minutes and 12 hours.",
      proposals: "Choose at least one window to offer.",
      prep: "Keep the note to {{max}} characters.",
      allowConflicts: "Tick Schedule anyway to double book the engineer.",
    },
    done: {
      scheduled: "{{number}} scheduled",
      rescheduled: "{{number}} rescheduled and the client told",
      proposed: "Windows offered for {{number}}",
      queued: "{{number}} added to the queue",
    },
  },
  detail: {
    back: "Field visits",
    backToVisits: "Back to field visits",
    loading: "Loading the visit",
    notFoundTitle: "Visit not found",
    notFoundBody: "It may have been removed, or the link is wrong.",
    title: "{{kind}}: {{organisation}}",
    chargeable: "Chargeable",
    noWindow: "Window to be chosen by the client",
    addToCalendar: "Add to calendar",
    downloadReport: "Download report",
    reportSaved: "Report saved",
    reschedule: "Reschedule",
    schedule: "Schedule",
    cancel: "Cancel visit",
    progress: "Visit progress",
    step: { done: "done", current: "current step", pending: "not yet" },
    cancelled: "This visit was cancelled. The client was told.",
    cancelledReason: "This visit was cancelled: {{reason}}",
    waitingClient: "Waiting for the client to choose one of the offered windows (chat, portal or e-mail).",
    waitingWindow: "This visit has no window yet. Schedule it, or offer the client up to three.",
    proposals: "Proposed windows",
    proposalsHint: "The client picks one",
    noProposals: "No window has been offered yet.",
    option: "Option {{n}}",
    chosen: "Chosen",
    scheduleMyself: "Schedule it myself",
    details: "Visit details",
    window: "Window",
    crew: "Engineers",
    site: "Site",
    team: "Team",
    prep: "Preparation note for the client",
    internal: "Internal notes",
    travel: "Travel notes",
    template: "Checklist template",
    rescheduled: "Rescheduled",
    rescheduledCount_one: "{{count}} time",
    rescheduledCount_other: "{{count}} times",
    origin: "Came from",
    rescheduleRequested: "The client asked to move this visit.",
    rescheduleRequestedNote: "The client asked to move this visit: {{note}}",
    assets: "Equipment",
    noAssets: "No equipment selected.",
    ticket: "Ticket",
    contract: "Contract impact",
    noContract: "No contract covers this visit.",
    parts: "Parts for this visit",
    noParts: "No parts recorded.",
  },
  cancel: {
    title: "Cancel {{number}}?",
    body: "The client and the engineer are told, and the ticket goes back to the status it had.",
    reason: "Reason",
    problem: "Give a reason of at least {{min}} characters.",
    keep: "Keep visit",
    confirm: "Cancel visit",
    done: "{{number}} cancelled and the client told",
  },
  consumption: {
    title: "{{number}} is beyond the contract",
    body: "This visit used more than the contract allows. Count it anyway, or waive it. Either way the reason stays on the ledger.",
    open: "Decide",
    choice: "What to do with this visit",
    action: { count_anyway: "Count it anyway", waive: "Waive it" },
    actionHint: { count_anyway: "It is counted against the contract past its allowance and is no longer charged.", waive: "It is left out of the count and is not charged." },
    reason: "Reason",
    reasonHint: "At least {{min}} characters. It is kept on the ledger.",
    problem: "Give a reason of at least {{min}} characters.",
    confirm: "Save decision",
    counted: "{{number}} counted against the contract",
    waived: "{{number}} waived",
    state: { none: "No contract entitlement applies.", counted: "Counted against the contract.", waived: "Waived with a reason.", beyond_contract: "Beyond the contract; waiting for a decision." },
  },
  report: {
    banner: { completed: "Visit completed.", cannot_complete: "The engineer could not complete this visit.", no_access: "The engineer could not get in." },
    bannerResolved: "The issue is resolved.",
    bannerFollowUp: "Follow-up needed.",
    work: "Work and findings",
    checkIn: "Check-in",
    checkOut: "Check-out",
    onSite: "On site",
    performed: "Work performed",
    findings: "Findings",
    resolved: "Issue resolved",
    yes: "Yes",
    followUp: "Follow-up needed",
    followUpNote: "Follow-up",
    measurements: "Measurements",
    measurement: "Measurement",
    value: "Value",
    noMeasurements: "No measurements recorded.",
    checklist: "Checklist",
    noChecklist: "No checklist was used on this visit.",
    passed: "{{pass}} of {{total}} passed",
    result: { pass: "passed", fail: "failed", na: "not applicable", none: "not answered" },
    parts: "Parts used",
    noParts: "No parts were used.",
    part: "Part",
    qty: "Qty",
    coverage: "Coverage",
    coverageValue: { warranty: "Warranty", contract: "Contract", chargeable: "Chargeable" },
    removedSerial: "Removed serial",
    newSerial: "New serial",
    photos: "Photos",
    noPhotos: "No photos were taken.",
    clientSees: "Client sees this",
    time: "Time on site",
    timeError: "Time entries couldn't load.",
    noTime: "No time entries.",
    entry: "Entry",
    length: "Length",
    who: "Who",
    billable: "billable",
    activity: { remote: "Remote", travel: "Travel", on_site: "On site", admin: "Admin" },
    timeTotal: "{{total}} in total, {{billable}} billable.",
    duration: "{{hours}} h {{minutes}} min",
    signoff: "Client sign-off",
    signMethod: { drawn: "signed on the screen", typed: "typed name" },
    notSigned: "Not signed yet.",
    signRequested: "A sign-off was requested and is waiting for the client.",
  },
} as const;
